export const en = {
  nav: {
    home: "Home",
    services: "Services",
    about: "About",
    contact: "Contact",
    getInTouch: "Get in Touch",
  },
  hero: {
    overline: "Premium Boutique Consultancy",
    headline: "Complexity made simple.",
    subheadline: "Strategic clarity for brands, founders, and businesses ready to move forward with confidence.",
    cta: "Start a Conversation",
    secondaryCta: "Explore Services",
  },
  intro: {
    overline: "Who We Are",
    title: "A boutique consultancy for brands that refuse to stay stuck.",
    text: "CREE8 Solutions helps founders, brands, and businesses simplify complexity, improve digital experiences, sharpen positioning, and move forward with focused strategic guidance. We turn overwhelming challenges into clear, actionable paths forward.",
  },
  servicesOverview: {
    overline: "What We Do",
    title: "Three pillars of strategic clarity.",
    services: [
      {
        title: "UX Consultancy",
        description: "Better digital experiences through strategic UX thinking, usability, clarity, and smarter product decisions.",
        icon: "Layers",
      },
      {
        title: "Marketing & Strategy",
        description: "Sharper positioning, clearer messaging, and growth efforts aligned with smarter strategic direction.",
        icon: "Target",
      },
      {
        title: "1:1 Mentoring",
        description: "Personalized strategic guidance, honest feedback, and focused support for founders and professionals who want clarity and momentum.",
        icon: "Compass",
      },
    ],
    cta: "View All Services",
  },
  whyChooseUs: {
    overline: "Why CREE8",
    title: "Built different. On purpose.",
    points: [
      {
        title: "Multidisciplinary Thinking",
        description: "We combine UX, strategy, and creative thinking to solve problems from every angle.",
        icon: "Layers",
      },
      {
        title: "Boutique Focus",
        description: "Every engagement gets dedicated attention. No assembly lines, no junior handoffs.",
        icon: "Diamond",
      },
      {
        title: "Clarity Over Complexity",
        description: "We simplify what feels overwhelming into structured, actionable direction.",
        icon: "Eye",
      },
      {
        title: "International Perspective",
        description: "Bilingual, culturally fluent, and experienced across diverse markets and industries.",
        icon: "Globe",
      },
    ],
  },
  process: {
    overline: "How We Work",
    title: "From complexity to clarity in four steps.",
    steps: [
      {
        number: "01",
        title: "Listen & Understand",
        description: "We start by deeply understanding your challenge, context, and goals.",
      },
      {
        number: "02",
        title: "Analyze & Simplify",
        description: "We break down complexity into clear, structured insights.",
      },
      {
        number: "03",
        title: "Strategize & Create",
        description: "We develop focused strategies and actionable solutions.",
      },
      {
        number: "04",
        title: "Execute & Support",
        description: "We guide implementation and provide ongoing strategic support.",
      },
    ],
  },
  trust: {
    overline: "Our Commitment",
    title: "Your confidence is our priority.",
    guaranteeTitle: "30-Day Satisfaction Guarantee",
    guaranteeText: "We stand behind the quality of our work. If you're not satisfied with the outcomes within the first 30 days, we'll work with you to make it right. Every engagement starts with a clear contract so expectations, scope, and commitment are transparent for both sides.",
    points: [
      "Clear contracts before every engagement",
      "Transparent communication throughout",
      "Case-by-case satisfaction handling",
      "Fair resolution if expectations aren't met",
    ],
  },
  finalCta: {
    title: "Ready to simplify?",
    description: "Let's talk about your challenge and find a clear path forward.",
    cta: "Get in Touch",
  },
  servicesPage: {
    hero: {
      overline: "Our Services",
      title: "Strategic clarity across every touchpoint.",
      description: "We help brands, founders, and businesses simplify complexity and move forward with confidence.",
    },
    ux: {
      title: "UX Consultancy",
      description: "We help brands and businesses improve digital experiences through strategic UX thinking, usability, clarity, and better product decisions.",
      forWhomLabel: "Who it's for",
      forWhom: "Brands and businesses that want their digital products to be intuitive, effective, and aligned with user needs.",
      outcomesLabel: "Expected Outcomes",
      outcomes: [
        "Clearer user journeys and improved conversion paths",
        "Actionable UX audits and strategic recommendations",
        "Better product decisions informed by research and data",
        "Streamlined interfaces that reduce friction and confusion",
      ],
      cta: "Discuss Your UX Challenge",
    },
    marketing: {
      title: "Marketing & Strategy",
      description: "We help businesses sharpen positioning, clarify messaging, and align growth efforts with smarter strategic direction.",
      forWhomLabel: "Who it's for",
      forWhom: "Businesses that need to stand out, communicate their value clearly, and grow with purpose.",
      outcomesLabel: "Expected Outcomes",
      outcomes: [
        "A clear, differentiated brand positioning",
        "Messaging that resonates with your target audience",
        "Strategic marketing direction aligned with business goals",
        "Smarter growth efforts focused on what actually works",
      ],
      cta: "Sharpen Your Strategy",
    },
    mentoring: {
      title: "1:1 Mentoring",
      description: "Personalized strategic guidance, honest feedback, and focused support for founders, creatives, and professionals who want clarity and momentum.",
      forWhomLabel: "Who it's for",
      forWhom: "Founders, creatives, and professionals who want a strategic partner, not just advice.",
      outcomesLabel: "Expected Outcomes",
      outcomes: [
        "Clarity on your next steps and strategic priorities",
        "Honest, direct feedback from experienced perspective",
        "Structured thinking for complex decisions",
        "Accountability and momentum to move forward",
      ],
      cta: "Book a Session",
    },
    guarantee: {
      title: "Our Satisfaction Guarantee",
      text: "We're committed to delivering real value. All services are backed by our 30-day satisfaction guarantee, reviewed case by case. Mentoring sessions may be rescheduled with at least 48 hours notice.",
    },
  },
  aboutPage: {
    hero: {
      overline: "About Us",
      title: "Simplifying complexity since day one.",
      description: "CREE8 Solutions is a premium boutique consultancy built on a simple belief: the best solutions come from clarity, not complexity.",
    },
    story: {
      title: "Our Story",
      paragraphs: [
        "CREE8 Solutions was founded on the conviction that businesses don't need more noise \u2014 they need more clarity. In a world saturated with generic advice and cookie-cutter strategies, we chose a different path.",
        "We're a founder-led consultancy that combines multidisciplinary expertise in UX, marketing strategy, and mentoring to help brands and businesses cut through complexity and focus on what truly matters.",
        "Our approach is deliberate, strategic, and human. We take the time to understand each client's unique context before offering direction. No templates. No shortcuts. Just thoughtful, focused work that creates real momentum.",
      ],
    },
    values: {
      overline: "What Drives Us",
      title: "Our Values",
      items: [
        {
          title: "Clarity",
          description: "We believe the best strategies are the ones that can be clearly understood and confidently acted upon.",
        },
        {
          title: "Integrity",
          description: "We give honest feedback, set realistic expectations, and always prioritize what's genuinely best for our clients.",
        },
        {
          title: "Excellence",
          description: "We hold our work to the highest standard. Every deliverable, every session, every recommendation reflects our commitment to quality.",
        },
        {
          title: "Partnership",
          description: "We see every client relationship as a partnership. Your success is our success.",
        },
      ],
    },
  },
  contactPage: {
    hero: {
      overline: "Contact",
      title: "Let's start a conversation.",
      description: "Have a proposal, challenge, or business problem? We'd love to hear about it. Reach out and let's explore how we can help.",
    },
    form: {
      name: "Full Name",
      namePlaceholder: "Your full name",
      email: "Email Address",
      emailPlaceholder: "you@company.com",
      company: "Company / Brand",
      companyPlaceholder: "Your company or brand name",
      service: "Service Needed",
      servicePlaceholder: "Select a service",
      serviceOptions: [
        "UX Consultancy",
        "Marketing & Strategy",
        "1:1 Mentoring",
        "Not sure yet",
      ],
      budget: "Budget Range",
      budgetPlaceholder: "Select your budget",
      budgetOptions: [
        "Less than $2,000",
        "$2,000 \u2013 $5,000",
        "$5,000 \u2013 $10,000",
        "More than $10,000",
      ],
      message: "Your Message",
      messagePlaceholder: "Tell us about your project, challenge, or goals...",
      submit: "Send Message",
      sending: "Sending...",
      successTitle: "Message sent successfully.",
      successText: "We'll get back to you within 24\u201348 business hours.",
      errorText: "Something went wrong. Please try again or email us directly.",
    },
    info: {
      title: "Get in Touch",
      emailLabel: "Email",
      email: "hola@cree8solutions.org",
      phoneLabel: "Phone",
      phone: "+1 (929) 685-2145",
      addressLabel: "Address",
      address: ["Suite B, 8 The Green, Dover", "DE 19901", "United States"],
    },
  },
  footer: {
    description: "Premium boutique consultancy helping founders, brands, and businesses simplify complexity and move forward with strategic clarity.",
    quickLinks: "Quick Links",
    legal: "Legal",
    connect: "Connect",
    privacyPolicy: "Privacy Policy",
    cookiePolicy: "Cookie Policy",
    termsConditions: "Terms & Conditions",
    refundPolicy: "Refund Policy",
    copyright: "\u00a9 2026 Cree8 Solutions LLC. All rights reserved.",
    address: ["Suite B, 8 The Green, Dover", "DE 19901, United States"],
    email: "hola@cree8solutions.org",
    phone: "+1 (929) 685-2145",
  },
  cookieConsent: {
    text: "We use cookies to enhance your browsing experience and analyze site traffic.",
    accept: "Accept",
    decline: "Decline",
    policyLink: "Cookie Policy",
  },
  privacyPolicy: {
    title: "Privacy Policy",
    lastUpdated: "Last updated: January 2025",
    sections: [
      {
        title: "Introduction",
        content: 'Cree8 Solutions LLC ("CREE8 Solutions", "we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage our services.',
      },
      {
        title: "Information We Collect",
        content: "We may collect personal information that you voluntarily provide, including: your name, email address, phone number, company name, and any other information you choose to share through our contact forms or during service engagements. We also automatically collect certain technical information such as browser type, IP address, and browsing behavior through cookies.",
      },
      {
        title: "How We Use Your Information",
        content: "We use the information we collect to: respond to your inquiries and provide our services; communicate with you about projects, services, and updates; improve our website and service offerings; comply with legal obligations; and protect our business interests.",
      },
      {
        title: "Information Sharing",
        content: "We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our business, provided they agree to keep your information confidential.",
      },
      {
        title: "Data Security",
        content: "We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.",
      },
      {
        title: "Your Rights",
        content: "You have the right to: access, correct, or delete your personal information; opt out of marketing communications; request information about how your data is used. To exercise these rights, please contact us at hola@cree8solutions.org.",
      },
      {
        title: "Contact Us",
        content: "If you have questions about this Privacy Policy, please contact us at:\n\nCree8 Solutions LLC\nSuite B, 8 The Green, Dover\nDE 19901, United States\nEmail: hola@cree8solutions.org\nPhone: +1 (929) 685-2145",
      },
    ],
  },
  cookiePolicyPage: {
    title: "Cookie Policy",
    lastUpdated: "Last updated: January 2025",
    sections: [
      {
        title: "What Are Cookies",
        content: "Cookies are small text files that are stored on your device when you visit a website. They help the website recognize your device and remember certain information about your visit.",
      },
      {
        title: "How We Use Cookies",
        content: "We use cookies to: ensure our website functions properly; analyze website traffic and usage patterns; remember your preferences and settings; improve your browsing experience.",
      },
      {
        title: "Types of Cookies We Use",
        content: "Essential Cookies: Required for basic website functionality. These cannot be disabled.\n\nAnalytics Cookies: Help us understand how visitors interact with our website, allowing us to improve our content and services.\n\nPreference Cookies: Remember your settings and choices to provide a more personalized experience.",
      },
      {
        title: "Managing Cookies",
        content: "You can control and manage cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of our website. You can also use our cookie consent banner to accept or decline non-essential cookies.",
      },
      {
        title: "Contact Us",
        content: "If you have questions about our Cookie Policy, please contact us at hola@cree8solutions.org.",
      },
    ],
  },
  termsConditions: {
    title: "Terms & Conditions",
    lastUpdated: "Last updated: January 2025",
    sections: [
      {
        title: "Agreement to Terms",
        content: 'By accessing or using the services provided by Cree8 Solutions LLC ("CREE8 Solutions"), you agree to be bound by these Terms & Conditions. If you do not agree, please do not use our services.',
      },
      {
        title: "Services",
        content: "CREE8 Solutions provides digital consulting services including UX consultancy, marketing & strategy consulting, and 1:1 mentoring. The specific scope, deliverables, and terms of each engagement are defined in individual contracts signed before work begins.",
      },
      {
        title: "Contracts & Engagements",
        content: "Before any project or mentoring engagement begins, a contract is signed so that commitment, scope, expectations, and deliverables are clear for both sides. This ensures transparency and mutual understanding throughout the engagement.",
      },
      {
        title: "Payment Terms",
        content: "Payment terms, schedules, and methods are outlined in individual service contracts. All fees are in USD unless otherwise specified. Payment is expected according to the agreed schedule.",
      },
      {
        title: "Intellectual Property",
        content: "Unless otherwise stated in the service contract, deliverables created during an engagement become the client's property upon full payment. CREE8 Solutions retains the right to use non-confidential aspects of the work for portfolio and promotional purposes.",
      },
      {
        title: "Limitation of Liability",
        content: "CREE8 Solutions provides strategic consulting and advisory services. We are not liable for business outcomes, financial results, or implementation decisions made by clients based on our recommendations. Our liability is limited to the fees paid for the specific service in question.",
      },
      {
        title: "Governing Law",
        content: "These Terms & Conditions are governed by the laws of the State of Delaware, United States. Any disputes shall be resolved through good-faith negotiation first, and if necessary, through the appropriate courts of Delaware.",
      },
      {
        title: "Contact",
        content: "For questions about these Terms, please contact:\n\nCree8 Solutions LLC\nSuite B, 8 The Green, Dover\nDE 19901, United States\nEmail: hola@cree8solutions.org\nPhone: +1 (929) 685-2145",
      },
    ],
  },
  refundPolicy: {
    title: "Refund & Cancellation Policy",
    lastUpdated: "Last updated: January 2025",
    sections: [
      {
        title: "Overview",
        content: "At CREE8 Solutions, client satisfaction is our priority. Because we provide digital services and consulting engagements, each situation is handled on a case-by-case basis with fairness and transparency.",
      },
      {
        title: "30-Day Satisfaction Guarantee",
        content: "We offer a 30-day satisfaction guarantee on our consulting services. If you are not satisfied with the quality of our work within the first 30 days, contact us and we will work together to find a fair resolution. This may include revisions, additional support, or a partial refund depending on the circumstances.",
      },
      {
        title: "Mentoring Sessions",
        content: "Mentoring sessions may be rescheduled with at least 48 hours' advance notice at no additional cost. If less than 48 hours' notice is given, the full session fee may still apply. No-shows are considered used sessions.",
      },
      {
        title: "Project Cancellations",
        content: "If you need to cancel an ongoing project, please notify us as soon as possible. Fees for work already completed are non-refundable. Any unused portion of prepaid fees may be refunded at our discretion, depending on the stage of the engagement.",
      },
      {
        title: "Contracts & Clarity",
        content: "Before any project or mentoring engagement begins, a contract is signed so that commitment, scope, and expectations are clear for both sides. This helps prevent misunderstandings and ensures both parties are aligned from the start.",
      },
      {
        title: "How to Request a Refund",
        content: "To request a refund or discuss any concerns about your service, please contact us at:\n\nEmail: hola@cree8solutions.org\nPhone: +1 (929) 685-2145\n\nWe will review your request and respond within 5 business days.",
      },
    ],
  },
};
